// Context
import { useEffect, useRef, useState } from "react";
import Modal from "../common/Modal";
import { onPressEnter } from "../../utils/utils";
import { marked } from "marked";
import apiAdmin from "../../services/apiAdmin";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";

function ModalAISupport({ close, show }) {
  const { t } = useTranslation();
  const [socketId] = useState(crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState();
  const [pendingAnswer, setPendingAnswer] = useState("");
  const [loading, setLoading] = useState();
  const [threadId, setThreadId] = useState();
  const [socket, setSocket] = useState();
  const bottomRef = useRef();

  const sendMessage = async () => {
    if (loading) return;

    // Kept so the failure path can put it back: setPrompt("") below has already
    // cleared the composer by the time the POST rejects.
    const sent = prompt;
    const optimistic = {
      type: "user",
      message: sent,
    };
    messages.push(optimistic);
    setPrompt("");
    setLoading(true);
    setMessages([...messages]);
    setTimeout(() => {
      bottomRef.current?.scrollIntoView();
    }, 10);

    try {
      const data = await apiAdmin.sendChatMessage(sent, threadId, socketId);
      // The ANSWER arrives over the socket (textCompleted), which is what clears
      // `loading` -- resolving here only means the question was accepted. The
      // first message is the exception: its 2xx must carry the threadId every
      // later message is sent under. A 2xx without one is not "accepted" -- no
      // thread means no answer is coming, which is the same permanent spinner as
      // an outright failure, so it is treated as one (the catch below). Once a
      // thread exists the body is informational and may legitimately be empty.
      if (!threadId) {
        const newThreadId = data?.data?.threadId;
        if (!newThreadId) throw new Error("AI support: message accepted without a threadId (empty 2xx body)");
        setThreadId(newThreadId);
      }
    } catch (e) {
      // Nothing was delivered, so no textCompleted event is ever coming: without
      // this the "Answering..." indicator runs forever and the send button stays
      // disabled for the rest of the session, while the message sits in the
      // transcript looking sent. Drop the optimistic bubble (it was never
      // delivered -- showing it is a lie) by identity from the CURRENT state --
      // the closure's array is whatever this render captured, and the socket
      // handler appends to state concurrently -- hand the text back so a retry
      // costs no retyping, and say so out loud.
      setMessages((prev) => prev.filter((m) => m !== optimistic));
      setPrompt(sent);
      setLoading(false);
      // This console.error is load-bearing: the outage was only ever visible in
      // Sentry BECAUSE the rejection was unhandled, and catching it silently would
      // have closed that window. Message + status only -- never the raw axios
      // error, whose config.data carries the clinician's own words (the admin
      // interceptor keeps e.message PHI-free for exactly this reason). The status
      // is lifted onto e.status by that interceptor; the two `.cause` reads are
      // fallbacks for an interceptor-shaped error that predates e.status (an
      // older apiAdmin.js bundle parks the raw axios error on .cause without
      // lifting the status). They are NOT a fallback for an error that bypassed
      // the interceptor: a raw AxiosError keeps its status at e.response.status
      // (and, on current axios, e.status -- so the first read already covers
      // it) while its .cause is the underlying XHR/network error, which neither
      // .cause read would find a status on. Nothing bypasses the interceptor
      // today anyway -- every apiAdmin call goes through the same instance.
      //
      // "Visible in Sentry" is narrower here than it was for the unhandled
      // rejection, which reached Sentry directly: console.error goes through
      // utils/clientErrorFilter.js first, and that filter turns anything that
      // reads as an auth failure -- "Response: 401"/403 in the text, or that
      // status on an Error object -- into a breadcrumb, never an event, because
      // on the main API those mean an expired session the app already handles.
      // The admin service gets no such handling and its 401s are usually the
      // UPSTREAM credential (the outage above was 16x 401), but the filter cannot
      // tell the two apart. So: a 5xx, a 404 or an empty first-message 2xx makes
      // a Sentry event from this line; a 401/403 makes only a breadcrumb plus the
      // toast. Restoring events for that class is a filter change (an explicit
      // report-always marker), not something this call site can do -- the test
      // file pins the verdict per status so this paragraph stays true.
      const status = e?.status ?? e?.cause?.response?.status ?? e?.cause?.status;
      console.error("AI support message could not be delivered -", (e && e.message) || e, status ? "(HTTP " + status + ")" : "");
      toast.error(t("tickets.aiSupport.sendFailed"), { toastId: "aiSupportSendFailed" });
    }
  };

  useEffect(() => {
    if (show && !socket) {
      initSocket();
    }
    // eslint-disable-next-line
  }, [show, socket]);

  // Start socket
  const initSocket = () => {
    console.log("Init socket AI Chat with id " + socketId);

    marked.setOptions({
      gfm: true,
      breaks: true,
      sanitize: true,
    });

    const socket = new WebSocket(process.env.REACT_APP_ENDPOINT_ADMIN + "/connect/" + socketId);
    // Connection opened
    socket.addEventListener("open", (event) => {
      console.log("Socket Connection AI Chat started");
    });

    socket.addEventListener("close", (event) => {
      console.log("Socket Connection AI Chat closed");
      setTimeout(() => {
        initSocket();
      }, 1000);
    });

    // Listen for messages
    socket.addEventListener("message", (event) => {
      try {
        event = JSON.parse(event.data);
        if (event.type === "textDelta") {
          setPendingAnswer((pendingAnswer) => {
            pendingAnswer += event.data;
            return pendingAnswer;
          });
          setTimeout(() => {
            bottomRef.current?.scrollIntoView();
          }, 10);

          // scroll container
        } else if (event.type === "textCompleted") {
          setPendingAnswer("");
          let answer = event.data || "Error: No response";
          answer = answer.replace(/【.*?】/g, "");
          answer = marked.parse(answer);
          console.log(answer);

          answer = answer.replace(
            /<p>.*?<a\s+href=["'](?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})["'][^>]*>.*?<\/a>.*?<\/p>/gi,
            (match, videoId) => `
    <iframe width="100%"
      style="min-height: 300px"
      src="https://www.youtube.com/embed/${videoId}" 
      frameborder="0" 
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
      allowfullscreen>
    </iframe>`
          );

          messages.push({
            type: "bot",
            message: answer,
          });
          setMessages((messages) => {
            messages.push({
              type: "bot",
              message: answer,
            });

            return messages;
          });
          setTimeout(() => {
            bottomRef.current?.scrollIntoView();
          }, 10);
          setLoading(false);
        }

        setSocket(socket);
      } catch (e) {
        console.error(e);
        console.error("Event not parsable: " + JSON.stringify(event));
      }
    });
  };

  return (
    <Modal show={show} close={close} size="medium" title={t("tickets.aiSupport.title")} className={"ai-support-modal"}>
      <div className="messages-container">
        {messages.map((message, m) => (
          <div
            key={m}
            className={"message-chat " + message.type}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(message.message, {
                ADD_TAGS: ["iframe"],
                ADD_ATTR: ["allow", "allowfullscreen", "frameborder"],
              }),
            }}
          ></div>
        ))}

        {loading && <div className="answering">{t("tickets.aiSupport.answering")}</div>}
        {pendingAnswer && (
          <div className="message-chat bot">
            <ReactMarkdown>{pendingAnswer}</ReactMarkdown>
          </div>
        )}

        {messages.length === 0 && !pendingAnswer && (
          <div className="no-message">
            <i className="notranslate material-icons-outlined">support_agent</i>
            <h4>{t("tickets.aiSupport.howCanHelp")}</h4>
          </div>
        )}
        <div ref={bottomRef}></div>
      </div>
      <div className="modal-actions">
        <input
          type="text"
          placeholder={t("tickets.aiSupport.typeMessage")}
          className="ml-15"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={onPressEnter(sendMessage)}
        />
        <button className="round mr-10 ml-10" onClick={sendMessage} disabled={loading}>
          <i className="notranslate material-icons-outlined">send</i>
        </button>
      </div>
    </Modal>
  );
}

export default ModalAISupport;
